import { c as _c } from "react/compiler-runtime";
import { Box, Button, HStack, Icon, IconButton, Input, StackProps, Text, VStack } from '@chakra-ui/react';
import { CommentPendingAttachments, useCommentAttachments } from '@src/components/sco/Comments/CommentAttachments';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { useRotatingPlaceholder } from '@src/hooks/useRotatingPlaceholder';
import React, { FormEvent, memo, useCallback, useRef, useState } from 'react';
import { LuArrowUp, LuCircleDollarSign, LuRocket, LuSparkles, LuUsers, LuPresentation, LuHandshake, LuFileUp } from 'react-icons/lu';

/** Founder-oriented prompts for Wil / signup-request chat intake. */
export const DEFAULT_INTAKE_PLACEHOLDERS = ['How can we grow your startup today?', 'I need help building an investor-ready pitch deck', 'How do I find the right advisors for my board?', 'What should I focus on before my next fundraise?', 'How do I get my first 100 customers?', 'I need a clearer go-to-market plan this quarter'];
export type IntakeQuickStart = {
  id: string;
  label: string;
  /** Full prompt submitted when the chip is clicked. */
  prompt: string;
  icon?: React.ElementType;
};

/** Quick-start chips under the ask bar (funding / advisors / customers / deck). */
export const DEFAULT_INTAKE_QUICK_STARTS: IntakeQuickStart[] = [{
  id: 'funding',
  label: 'Raise Funding',
  prompt: 'What should I do to raise funding for my startup?',
  icon: LuCircleDollarSign
}, {
  id: 'advisors',
  label: 'Meet Advisors',
  prompt: 'How do I find and recruit the right advisors for my company?',
  icon: LuUsers
}, {
  id: 'customers',
  label: 'Get Customers',
  prompt: 'How can I get my first customers and grow traction?',
  icon: LuRocket
}, {
  id: 'pitch-deck',
  label: 'Pitch Deck',
  prompt: 'How do I build a strong investor pitch deck?',
  icon: LuPresentation
}, {
  id: 'hiring',
  label: 'Hire Employees',
  prompt: 'How do I find and recruit the right employees for my company?',
  icon: LuSparkles
}, {
  id: 'co-founder',
  label: 'Find a Co-founder',
  prompt: 'How do I find and recruit the right co-founder for my company?',
  icon: LuHandshake
}];
export type SignupRequestChatIntakeAskMeta = {
  storedFileIds: string[];
};
export type SignupRequestChatIntakeAskBarProps = {
  /** Static override — disables rotation when set. */
  placeholder?: string;
  /** Rotating placeholder strings (default DEFAULT_INTAKE_PLACEHOLDERS). */
  placeholders?: string[];
  /** Quick-start chips under the input (default DEFAULT_INTAKE_QUICK_STARTS). */
  quickStarts?: IntakeQuickStart[];
  /** Hide quick-start chips. */
  hideQuickStarts?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  /**
   * Allow document upload without a registered session (guest homepage intake).
   * Server only accepts this when the matching flag is sent; default stays fail-closed.
   */
  bypassUploadAuth?: boolean;
  /** Hide the attach-file control. */
  hideFileAttach?: boolean;
  onAsk?: (query: string, meta?: SignupRequestChatIntakeAskMeta) => void | Promise<void>;
  containerProps?: StackProps;
};

/**
 * Isolated ask input for SignupRequestChatIntake.
 * Owns typing + rotating placeholder state so parent GraphQL/bootstrap re-renders
 * do not thrash the input while the placeholder animates.
 */
export const SignupRequestChatIntakeAskBar = memo(function SignupRequestChatIntakeAskBar(t0) {
  const $ = _c(107);
  const {
    placeholder: staticPlaceholder,
    placeholders: t1,
    quickStarts: t2,
    hideQuickStarts: t3,
    defaultValue: t4,
    disabled: t5,
    bypassUploadAuth: t6,
    hideFileAttach: t7,
    onAsk,
    containerProps
  } = t0;
  const placeholders = t1 === undefined ? DEFAULT_INTAKE_PLACEHOLDERS : t1;
  const quickStarts = t2 === undefined ? DEFAULT_INTAKE_QUICK_STARTS : t2;
  const hideQuickStarts = t3 === undefined ? false : t3;
  const defaultValue = t4 === undefined ? "" : t4;
  const disabled = t5 === undefined ? false : t5;
  const bypassUploadAuth = t6 === undefined ? false : t6;
  const hideFileAttach = t7 === undefined ? false : t7;
  const [value, setValue] = useState(defaultValue);
  const fileInputRef = useRef(null);
  let t8;
  if ($[0] !== bypassUploadAuth) {
    t8 = {
      bypassAuth: bypassUploadAuth
    };
    $[0] = bypassUploadAuth;
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const {
    pendingFiles,
    addFile,
    removeFile,
    reset,
    isUploading,
    isFull,
    storedFileIds
  } = useCommentAttachments(t8);
  let t9;
  if ($[2] !== value) {
    t9 = value.trim() ? value : undefined;
    $[2] = value;
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  let t10;
  if ($[4] !== t9) {
    t10 = {
      shuffle: false,
      interval: 3000,
      value: t9
    };
    $[4] = t9;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  const rotatingPlaceholder = useRotatingPlaceholder(placeholders, t10);
  const placeholder = staticPlaceholder || rotatingPlaceholder;
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t11;
  if ($[6] !== getBreakpointValue) {
    t11 = getBreakpointValue(["40px", "44px", "48px"]);
    $[6] = getBreakpointValue;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const actionSize = t11;
  let t12;
  if ($[8] !== getBreakpointValue) {
    t12 = getBreakpointValue([3, 4, 5]);
    $[8] = getBreakpointValue;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  const inputPaddingX = t12;
  let t13;
  if ($[10] !== getBreakpointValue) {
    t13 = getBreakpointValue([2.5, 3, 4]);
    $[10] = getBreakpointValue;
    $[11] = t13;
  } else {
    t13 = $[11];
  }
  const inputPaddingY = t13;
  let t14;
  if ($[12] !== disabled || $[13] !== isUploading || $[14] !== onAsk || $[15] !== reset || $[16] !== storedFileIds) {
    t14 = async query => {
      if (disabled || isUploading) {
        return;
      }
      const trimmed = query.trim();
      if (!trimmed) {
        return;
      }
      setValue(trimmed);
      const fileIds = [...storedFileIds];
      await onAsk?.(trimmed, {
        storedFileIds: fileIds
      });
      reset();
    };
    $[12] = disabled;
    $[13] = isUploading;
    $[14] = onAsk;
    $[15] = reset;
    $[16] = storedFileIds;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const submitQuery = t14;
  let t15;
  if ($[18] !== submitQuery || $[19] !== value) {
    t15 = event => {
      event.preventDefault();
      submitQuery(value);
    };
    $[18] = submitQuery;
    $[19] = value;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const handleSubmit = t15;
  let t16;
  if ($[21] !== submitQuery) {
    t16 = item => {
      submitQuery(item.prompt);
    };
    $[21] = submitQuery;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const handleQuickStart = t16;
  let t17;
  if ($[23] !== addFile || $[24] !== disabled || $[25] !== isFull || $[26] !== isUploading) {
    t17 = event_0 => {
      const file = event_0.target.files?.[0];
      event_0.target.value = "";
      if (!file || disabled || isFull || isUploading) {
        return;
      }
      addFile(file);
    };
    $[23] = addFile;
    $[24] = disabled;
    $[25] = isFull;
    $[26] = isUploading;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const handleFileChange = t17;
  let t18;
  if ($[28] !== disabled || $[29] !== isUploading || $[30] !== value) {
    t18 = !!value.trim() && !disabled && !isUploading;
    $[28] = disabled;
    $[29] = isUploading;
    $[30] = value;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const canSubmit = t18;
  let t19;
  if ($[32] !== getBreakpointValue) {
    t19 = getBreakpointValue([3, 3, 4]);
    $[32] = getBreakpointValue;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = disabled ? 0.6 : 1;
  let t21;
  if ($[34] !== getBreakpointValue) {
    t21 = getBreakpointValue([1, 2, 2]);
    $[34] = getBreakpointValue;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  const t22 = disabled ? "none" : "auto";
  let t23;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      borderColor: "orange.400"
    };
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== getBreakpointValue) {
    t24 = getBreakpointValue([2, 2, 3]);
    $[37] = getBreakpointValue;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== getBreakpointValue) {
    t25 = getBreakpointValue(["56px", "64px", "70px"]);
    $[39] = getBreakpointValue;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== getBreakpointValue) {
    t26 = getBreakpointValue([2, 3, 4]);
    $[41] = getBreakpointValue;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] !== getBreakpointValue) {
    t27 = getBreakpointValue([4, 5, 5]);
    $[43] = getBreakpointValue;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== t27) {
    t28 = <Icon aria-hidden={true} as={LuSparkles} boxSize={t27} color="orange.400" flexShrink={0} />;
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  let t30;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      boxShadow: "none",
      borderColor: "transparent"
    };
    t30 = {
      color: "fg.subtle"
    };
    $[47] = t29;
    $[48] = t30;
  } else {
    t29 = $[47];
    t30 = $[48];
  }
  const t31 = placeholder || "Ask a question";
  let t32;
  if ($[49] !== getBreakpointValue) {
    t32 = getBreakpointValue(["md", "md", "lg"]);
    $[49] = getBreakpointValue;
    $[50] = t32;
  } else {
    t32 = $[50];
  }
  let t33;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = event_1 => setValue(event_1.target.value);
    $[51] = t33;
  } else {
    t33 = $[51];
  }
  let t34;
  if ($[52] !== disabled || $[53] !== placeholder || $[54] !== t31 || $[55] !== t32 || $[56] !== value) {
    t34 = <Input _focusVisible={t29} _placeholder={t30} aria-label={t31} border="none" boxShadow="none" color="fg" disabled={disabled} flex={1} fontSize={t32} minWidth={0} placeholder={placeholder} value={value} variant="flushed" onChange={t33} />;
    $[52] = disabled;
    $[53] = placeholder;
    $[54] = t31;
    $[55] = t32;
    $[56] = value;
    $[57] = t34;
  } else {
    t34 = $[57];
  }
  let t35;
  if ($[58] !== getBreakpointValue) {
    t35 = getBreakpointValue([1, 1.5, 2]);
    $[58] = getBreakpointValue;
    $[59] = t35;
  } else {
    t35 = $[59];
  }
  let t36;
  if ($[60] !== actionSize || $[61] !== disabled || $[62] !== handleFileChange || $[63] !== hideFileAttach || $[64] !== isFull || $[65] !== isUploading) {
    t36 = !hideFileAttach && <><IconButton _hover={{
        color: "fg"
      }} aria-label="Attach file" borderRadius="full" boxSize={actionSize} color="fg.muted" disabled={disabled || isFull || isUploading} minWidth={actionSize} title="Attach file" type="button" variant="subtle" onClick={() => fileInputRef.current?.click()}><Icon as={LuFileUp} color="inherit" /></IconButton><input ref={fileInputRef} accept="*/*" style={{
        display: "none"
      }} type="file" onChange={handleFileChange} /></>;
    $[60] = actionSize;
    $[61] = disabled;
    $[62] = handleFileChange;
    $[63] = hideFileAttach;
    $[64] = isFull;
    $[65] = isUploading;
    $[66] = t36;
  } else {
    t36 = $[66];
  }
  const t37 = !canSubmit;
  let t38;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <LuArrowUp />;
    $[67] = t38;
  } else {
    t38 = $[67];
  }
  let t39;
  if ($[68] !== actionSize || $[69] !== isUploading || $[70] !== t37) {
    t39 = <IconButton aria-label="Ask" borderRadius="full" boxSize={actionSize} disabled={t37} flexShrink={0} loading={isUploading} minWidth={actionSize} type="submit" variant="cta">{t38}</IconButton>;
    $[68] = actionSize;
    $[69] = isUploading;
    $[70] = t37;
    $[71] = t39;
  } else {
    t39 = $[71];
  }
  let t40;
  if ($[72] !== t35 || $[73] !== t36 || $[74] !== t39) {
    t40 = <HStack flexShrink={0} gap={t35}>{t36}{t39}</HStack>;
    $[72] = t35;
    $[73] = t36;
    $[74] = t39;
    $[75] = t40;
  } else {
    t40 = $[75];
  }
  let t41;
  if ($[76] !== inputPaddingX || $[77] !== inputPaddingY || $[78] !== t24 || $[79] !== t25 || $[80] !== t26 || $[81] !== t28 || $[82] !== t34 || $[83] !== t40) {
    t41 = <HStack align="center" gap={t24} minHeight={t25} paddingLeft={inputPaddingX} paddingRight={t26} paddingY={inputPaddingY} transition="border-color 0.15s ease" width="full">{t28}{t34}{t40}</HStack>;
    $[76] = inputPaddingX;
    $[77] = inputPaddingY;
    $[78] = t24;
    $[79] = t25;
    $[80] = t26;
    $[81] = t28;
    $[82] = t34;
    $[83] = t40;
    $[84] = t41;
  } else {
    t41 = $[84];
  }
  let t42;
  if ($[85] !== disabled || $[86] !== pendingFiles || $[87] !== removeFile) {
    t42 = pendingFiles?.length > 0 && <Box bg="colorPalette.bg" borderBottomRadius="2xl" borderColor="colorPalette.border" borderTop="2px" borderTopWidth="1px" paddingBottom={2} paddingX={4}><HStack><Text fontSize="sm" paddingTop={2}>Files:{" "}</Text><CommentPendingAttachments disabled={disabled} files={pendingFiles} onRemove={removeFile} /></HStack></Box>;
    $[85] = disabled;
    $[86] = pendingFiles;
    $[87] = removeFile;
    $[88] = t42;
  } else {
    t42 = $[88];
  }
  let t43;
  if ($[89] !== handleSubmit || $[90] !== t41 || $[91] !== t42) {
    t43 = <VStack align="stretch" gap={0} width="full"><VStack _focusWithin={t23} align="stretch" as="form" bg="bg.subtle" borderColor="colorPalette.border" borderRadius="2xl" borderWidth="2px" onSubmit={handleSubmit}>{t41}{t42}</VStack></VStack>;
    $[89] = handleSubmit;
    $[90] = t41;
    $[91] = t42;
    $[92] = t43;
  } else {
    t43 = $[92];
  }
  let t44;
  if ($[93] !== disabled || $[94] !== handleQuickStart || $[95] !== hideQuickStarts || $[96] !== isUploading || $[97] !== quickStarts) {
    t44 = !hideQuickStarts && quickStarts.length > 0 && <HStack aria-label="Quick start topics" flexWrap="wrap" gap={2} justify="center" role="group" width="full">{quickStarts.map(item_0 => <Button key={item_0.id} borderRadius="full" disabled={disabled || isUploading} size="xs" type="button" variant="outlineSolid" onClick={() => handleQuickStart(item_0)}>{item_0.icon ? <Icon as={item_0.icon} boxSize={3.5} /> : null}{item_0.label}</Button>)}</HStack>;
    $[93] = disabled;
    $[94] = handleQuickStart;
    $[95] = hideQuickStarts;
    $[96] = isUploading;
    $[97] = quickStarts;
    $[98] = t44;
  } else {
    t44 = $[98];
  }
  let t45;
  if ($[99] !== containerProps || $[100] !== t19 || $[101] !== t20 || $[102] !== t21 || $[103] !== t22 || $[104] !== t43 || $[105] !== t44) {
    t45 = <VStack align="stretch" as="section" gap={t19} opacity={t20} paddingBottom={t21} pointerEvents={t22} width="full" {...containerProps}>{t43}{t44}</VStack>;
    $[99] = containerProps;
    $[100] = t19;
    $[101] = t20;
    $[102] = t21;
    $[103] = t22;
    $[104] = t43;
    $[105] = t44;
    $[106] = t45;
  } else {
    t45 = $[106];
  }
  return t45;
});